import {
  useIsFocused,
  useNavigation,
  useScrollToTop,
} from '@react-navigation/native';
import React, {useEffect, useRef, useState} from 'react';
import {
  Animated,
  BackHandler,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import {useTheme} from 'styled-components/native';
import BottomSheet from '../../components/Interface/BottomSheet';
import {
  consumePendingPayment,
  type PendingPayment,
} from '../../utils/payments/pendingPayment';

import {
  AccountMenuContainer,
  Container,
  Content,
  Divider,
  EmprestimoAlertCard,
  EmprestimoAlertContent,
  EmprestimoAlertIcon,
  EmprestimoAlertIconWrapper,
  EmprestimoAlertSubtitle,
  EmprestimoAlertTitle,
  SectionContainer,
  EmprestimoAlertChevron,
} from './styles';

import {GreenHeader} from '../../components/Interface/Headers/GreenHeader';
import {RFValue} from 'react-native-responsive-fontsize';
import {HomeServices} from './components/HomeServices';

import analytics from '@react-native-firebase/analytics';

import {useAuth} from '../../context/auth';
import {AppEventsLogger} from 'react-native-fbsdk-next';

import {useGlobal} from '../../context/global';
import {addInAppTrigger} from '../../lib/pushMessaging';
import {api} from '../../services/api';
import {rastrear} from '../../services/rastrear';
import {AccountMenusGrid} from './components/AccountMenusGrid';
import {ClubeAcessoModal} from '../../components/Interface/ClubeAcessoModal';
import {AvalieGanheBottomSheet} from '../../components/shared/AvalieGanheBottomSheet';
import {InfluencerBottomSheet} from './components/InfluencerBottomSheet';

import {ClubeSection} from './components/ClubeSection';
import {HomeBannerCarousel} from './components/HomeBannersCarousel';
import {FipeSection} from './components/FipeSection';
//import {NearbyStationSuggestion} from './components/NearbyStationSuggestion';
import {useEmprestimoAlerts} from '../../context/EmprestimoAlerts';
import {useIdpayVerification} from '../../context/IdpayVerification';
import {IdpayVerificationGate} from '../../components/shared/IdpayVerificationBottomSheet';
import {FocusAwareStatusBar} from '../../context/statusbar';
import {SavedContent} from '../../components/shared/SavedContent';

type CodigoSolicitado = 'codigo' | 'placa';

type StepTwoConfig = {
  label: string;
  placeholder: string;
  subtitle: string;
};

const defaultStepTwoConfig: StepTwoConfig = {
  label: 'Código pessoal',
  placeholder: 'Informe seu código',
  subtitle:
    'Cupom válido! Agora informe seu código pessoal dentro do clube para liberar o acesso às vantagens.',
};

const PLACA_MASK = '[AAA]-[0AAA0]';
const PLACA_AFFINE_MASKS = ['[AAA]-[0000]'];

export function Home() {
  const {isLogged, fetchUserData, user, openSign} = useAuth();
  const {fetchConfigIndiqueGanhe, disabledFeatures} = useGlobal();
  const isDisabled = (key: string) => disabledFeatures.includes(key);
  const {alertType, alertParcelaCount} = useEmprestimoAlerts();
  const {refreshOnce: refreshIdpayOnce} = useIdpayVerification();

  const isFocused = useIsFocused();

  const navigate = useNavigation();
  const theme = useTheme();

  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(
    null,
  );

  const [showPendingModal, setShowPendingModal] = useState(false);

  const [showClubeModal, setShowClubeModal] = useState(false);
  const [showAvalieModal, setShowAvalieModal] = useState(false);
  const [showInfluencerModal, setShowInfluencerModal] = useState(false);
  const [codigoSolicitado, setCodigoSolicitado] =
    useState<CodigoSolicitado>('codigo');
  const [stepTwoConfig, setStepTwoConfig] = useState<StepTwoConfig>({
    ...defaultStepTwoConfig,
  });

  const shouldMaskPlaca = codigoSolicitado === 'placa';

  useEffect(() => {
    consumePendingPayment().then(pending => {
      if (!pending) {
        return;
      }
      setPendingPayment(pending);
      setShowPendingModal(true);
    });
  }, []);

  useEffect(() => {
    if (isFocused) {
      refreshIdpayOnce();
    }
  }, [isFocused, refreshIdpayOnce]);

  const handleContinuePurchase = () => {
    setShowPendingModal(false);
    if (!pendingPayment) {
      return;
    }
    try {
      (navigate as any).reset(pendingPayment.state);
    } catch {}
  };
  //const { INDIQUE_GANHE_TUTORIAL_STORAGE_KEY, startIndiqueGanheTutorial } = useTutorial();

  useEffect(() => {
    AppEventsLogger.logEvent('abriu_app');
  }, []);

  useEffect(() => {
    if (user.user_id) {
      fetchUserData();
    }
    fetchConfigIndiqueGanhe();

    if (isFocused) {
      rastrear({status_tela: 'home'});
    }
  }, [isFocused]);

  const ref = React.useRef(null);
  useScrollToTop(ref);

  const scrollY = useRef(new Animated.Value(0)).current;

  if (Platform.OS !== 'web') {
    addInAppTrigger('auth', 'logged_in');
  }
  useEffect(() => {
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => false,
    );
    return () => subscription.remove();
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      addInAppTrigger('screen', 'home');
    }
  }, []);

  const handleVipClubPress = () => {
    if (!isLogged) {
      return openSign();
    }
    resetClubeAccessState();
    analytics().logEvent('home_clica_vip_club');
    setShowClubeModal(true);
  };

  const handleAvalieGanhePress = () => {
    setShowAvalieModal(true);
  };

  function resetClubeAccessState() {
    setCodigoSolicitado('codigo');
    setStepTwoConfig({...defaultStepTwoConfig});
  }

  function handleCloseClubeModal() {
    resetClubeAccessState();
    setShowClubeModal(false);
  }

  function handleCodigoChange(formatted: string, extracted?: string) {
    if (!shouldMaskPlaca) {
      return undefined;
    }
    const sanitized = (extracted || formatted || '')
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase();
    return sanitized;
  }

  async function validateCupom(cupom: string) {
    const normalizedCupom = cupom.trim().toLowerCase().replace(/\s+/g, '');

    if (normalizedCupom.length < 3) {
      return {ok: false, message: 'Digite um cupom válido para continuar.'};
    }

    try {
      const {data} = await api.get('/solicitaPlaca', {
        params: {codigoDesconto: normalizedCupom, masterclin: true},
      });

      if (!data) {
        await api.get('/clube_masterclin', {
          params: {codigoDesconto: normalizedCupom},
        });
        handleClubeSuccess(normalizedCupom, '');
        return {ok: true, complete: true, codigo: ''};
      }

      const solicitacao: CodigoSolicitado =
        data === 'placa' ? 'placa' : 'codigo';
      setCodigoSolicitado(solicitacao);

      if (solicitacao === 'placa') {
        setStepTwoConfig({
          label: 'Placa do veículo',
          placeholder: 'Digite a placa solicitada',
          subtitle:
            'Cupom válido! Agora informe a placa solicitada para liberar o acesso ao clube.',
        });
      } else {
        setStepTwoConfig({...defaultStepTwoConfig});
      }

      return {ok: true};
    } catch (error: any) {
      const message =
        error?.response?.data?.msg ||
        error?.response?.data?.error ||
        'Não conseguimos validar o cupom. Tente novamente.';
      return {ok: false, message};
    }
  }

  async function validateCodigoPessoal(codigoPessoal: string, cupom?: string) {
    const codigoLimpo =
      codigoSolicitado === 'placa'
        ? codigoPessoal.trim().replace(/\s+/g, '').toUpperCase()
        : codigoPessoal.trim();

    const cupomNormalizado = cupom
      ? cupom.trim().toLowerCase().replace(/\s+/g, '')
      : '';

    if (codigoLimpo.length < 3) {
      return {
        ok: false,
        message:
          codigoSolicitado === 'placa'
            ? 'Revise a placa informada.'
            : 'Revise seu código pessoal dentro do clube.',
      };
    }

    if (!cupomNormalizado) {
      return {ok: false, message: 'Digite um cupom válido para continuar.'};
    }

    try {
      await api.get('/clube_masterclin', {
        params:
          codigoSolicitado === 'placa'
            ? {codigoDesconto: cupomNormalizado, placa: codigoLimpo}
            : {codigoDesconto: cupomNormalizado, codigo: codigoLimpo},
      });
      return {ok: true};
    } catch (error: any) {
      const message =
        error?.response?.data?.msg ||
        error?.response?.data?.error ||
        (codigoSolicitado === 'placa'
          ? 'Não foi possível validar a placa informada.'
          : 'Não foi possível validar o código informado.');
      return {ok: false, message};
    }
  }

  function handleClubeSuccess(cupom: string, codigoPessoal: string) {
    analytics().logEvent('clube_validacao_sucesso', {
      cupom,
      codigo_pessoal: codigoPessoal,
    });
    resetClubeAccessState();
    setShowClubeModal(false);
    navigate.navigate('LoadingClube');
  }

  return (
    <>
      <FocusAwareStatusBar barStyle="light-content" />
      <GreenHeader scrollY={scrollY} />

      <SavedContent />

      <Container
        showsVerticalScrollIndicator={false}
        ref={ref}
        contentContainerStyle={{paddingBottom: RFValue(120), flexGrow: 1}}
        onScroll={(e: any) => scrollY.setValue(e.nativeEvent.contentOffset.y)}
        scrollEventThrottle={16}
        enableOnAndroid
        extraScrollHeight={RFValue(24)}>
        <Content
          style={{
            paddingTop: RFValue(35),
          }}>
          <AccountMenuContainer>
            <AccountMenusGrid onAvalieGanhePress={handleAvalieGanhePress} />
          </AccountMenuContainer>

          {/* <NearbyStationSuggestion /> */}

          {isLogged &&
            (alertType === 'vencidas' || alertType === 'a_vencer') && (
              <SectionContainer style={{paddingBottom: 12}}>
                <Animated.View>
                  <EmprestimoAlertCard
                    variant={alertType === 'vencidas' ? 'red' : 'orange'}
                    activeOpacity={0.85}
                    onPress={() =>
                      navigate.navigate('Emprestimo', {
                        initialTab: 'emprestimos',
                      })
                    }>
                    <EmprestimoAlertIconWrapper
                      variant={alertType === 'vencidas' ? 'red' : 'orange'}>
                      <EmprestimoAlertIcon
                        variant={alertType === 'vencidas' ? 'red' : 'orange'}
                        name={
                          alertType === 'vencidas' ? 'alert-circle' : 'clock'
                        }
                      />
                    </EmprestimoAlertIconWrapper>
                    <EmprestimoAlertContent>
                      <EmprestimoAlertTitle
                        variant={alertType === 'vencidas' ? 'red' : 'orange'}>
                        {alertParcelaCount != null
                          ? alertParcelaCount === 1
                            ? alertType === 'vencidas'
                              ? '1 parcela em atraso'
                              : '1 parcela a vencer'
                            : alertType === 'vencidas'
                            ? `${alertParcelaCount} parcelas em atraso`
                            : `${alertParcelaCount} parcelas a vencer`
                          : alertType === 'vencidas'
                          ? 'Parcelas em atraso'
                          : 'Parcelas a vencer'}
                      </EmprestimoAlertTitle>
                      <EmprestimoAlertSubtitle>
                        {alertType === 'vencidas'
                          ? 'Pague e evite juros e encargos'
                          : 'Pague antes e evite juros'}
                      </EmprestimoAlertSubtitle>
                    </EmprestimoAlertContent>
                    <Animated.View>
                      <EmprestimoAlertChevron
                        variant={alertType === 'vencidas' ? 'red' : 'orange'}
                        name="chevron-right"
                      />
                    </Animated.View>
                  </EmprestimoAlertCard>
                </Animated.View>
              </SectionContainer>
            )}

          <HomeServices
            splitAt={4}
            middleContent={
              <HomeBannerCarousel onOpenClubeModal={handleVipClubPress} />
            }
            onVipClubPress={handleVipClubPress}
          />



          {!isDisabled('fipe') && (
            <>
              <Divider />

              <FipeSection />


            </>
          )}

          {!isDisabled('vantagens') && (
            <>

               <Divider />
              <ClubeSection />


            </>
          )}
        </Content>
      </Container>

      <BottomSheet
        isOpen={showPendingModal}
        onClose={() => setShowPendingModal(false)}>
        <View style={modalStyles.container}>
          <Text style={modalStyles.icon}>🛒</Text>
          <Text style={[modalStyles.title, {color: theme.colors.title}]}>
            Sua compra está esperando
          </Text>
          <Text style={[modalStyles.subtitle, {color: theme.colors.text}]}>
            {pendingPayment?.screen === 'PP_Checkout'
              ? 'Você estava finalizando o pagamento de débitos veiculares.'
              : 'Você estava finalizando um pagamento no app.'}
          </Text>
          <TouchableOpacity
            style={[
              modalStyles.primaryButton,
              {backgroundColor: theme.colors.primary},
            ]}
            onPress={handleContinuePurchase}
            activeOpacity={0.85}>
            <Text style={modalStyles.primaryButtonText}>Continuar compra</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={modalStyles.ghostButton}
            onPress={() => setShowPendingModal(false)}
            activeOpacity={0.7}>
            <Text
              style={[modalStyles.ghostButtonText, {color: theme.colors.text}]}>
              Agora não
            </Text>
          </TouchableOpacity>
        </View>
      </BottomSheet>

      <ClubeAcessoModal
        isVisible={showClubeModal}
        onDismiss={handleCloseClubeModal}
        onSuccess={handleClubeSuccess}
        validateCupom={validateCupom}
        validateCodigoPessoal={validateCodigoPessoal}
        codigoLabel={stepTwoConfig.label}
        codigoPlaceholder={stepTwoConfig.placeholder}
        stepTwoSubtitle={stepTwoConfig.subtitle}
        codigoMask={shouldMaskPlaca ? PLACA_MASK : undefined}
        codigoMaskAffineFormats={
          shouldMaskPlaca ? PLACA_AFFINE_MASKS : undefined
        }
        onCodigoChange={shouldMaskPlaca ? handleCodigoChange : undefined}
      />

      <AvalieGanheBottomSheet
        isOpen={showAvalieModal}
        onClose={() => setShowAvalieModal(false)}
      />

      <InfluencerBottomSheet
        isOpen={showInfluencerModal}
        onClose={() => setShowInfluencerModal(false)}
      />

      <IdpayVerificationGate />
    </>
  );
}

const modalStyles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 8,
    alignItems: 'center',
  },
  icon: {
    fontSize: 48,
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontFamily: 'Poppins-SemiBold',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    fontFamily: 'Poppins-Regular',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 28,
  },
  primaryButton: {
    width: '100%',
    paddingVertical: 16,
    borderRadius: 14,
    alignItems: 'center',
    marginBottom: 12,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: 'Poppins-SemiBold',
  },
  ghostButton: {
    paddingVertical: 12,
    alignItems: 'center',
    width: '100%',
  },
  ghostButtonText: {
    fontSize: 14,
    fontFamily: 'Poppins-Regular',
  },
});
